import { useEffect, useRef } from 'react'
import { useMap } from 'react-leaflet'
import type { Location } from '@hooks/useGeolocation'

interface CenterOnUserProps {
  location: Location | null
  /** Zoom to use when centring. Roughly a city at 11. */
  zoom?: number
}

/**
 * Puts the map where the operator is, once.
 *
 * Once is the whole design. The geolocation hook keeps a `watchPosition` running and reports every
 * significant move, and a map that re-centred on each of those would drag itself out from under
 * anyone who had panned somewhere to look at it. So the first fix wins and the rest are ignored;
 * the "My location" button exists for deliberately going back.
 *
 * This cannot be done with the map's `center` prop instead: Leaflet reads that once at mount, and
 * a position that arrives from the geolocation API a second later — which is the normal case, and
 * the only case on a cold permission prompt — would be silently dropped.
 */
export function CenterOnUser({ location, zoom = 11 }: CenterOnUserProps) {
  const map = useMap()
  const centred = useRef(false)

  useEffect(() => {
    if (!location || centred.current) return

    centred.current = true
    map.setView([location.latitude, location.longitude], zoom)
  }, [location, map, zoom])

  return null
}
